import type { Round } from '@h2h/shared';
import { shortDay, timeLabel } from '../helpers';

export type StagePhaseTone = 'upcoming' | 'open' | 'live' | 'settling' | 'settled';

/** Map round lifecycle → a small set of UI tones for pills, banners and row chrome. */
export function stagePhaseTone(status?: Round['status']): StagePhaseTone {
  switch (status) {
    case 'OPEN':
      return 'open';
    case 'LOCKING':
      return 'live';
    case 'SETTLING':
      return 'settling';
    case 'SETTLED':
      return 'settled';
    default:
      return 'upcoming';
  }
}

export const STAGE_PHASE_PILL: Record<StagePhaseTone, { label: string; className: string }> = {
  upcoming: { label: 'Upcoming', className: 'bg-[#EFEFF3] text-[#6E6E73]' },
  open: { label: 'Picks open', className: 'bg-accent-wash text-accent-ink' },
  live: { label: 'Stage live', className: 'bg-[rgba(227,168,43,0.18)] text-[#B5740A]' },
  settling: { label: 'Scoring', className: 'bg-[rgba(227,168,43,0.18)] text-[#B5740A]' },
  settled: { label: 'Finished', className: 'bg-[#EFEFF3] text-[#6E6E73]' }
};

/** Split "Stage 3 | Bad Ragaz - Bad Ragaz" into a title + route line. */
export function parseStageLabel(label: string): { heading: string; route: string | null } {
  const pipe = label.indexOf('|');
  if (pipe >= 0) {
    const route = label
      .slice(pipe + 1)
      .trim()
      .replace(/\s*-\s*/g, ' → ');
    return { heading: label.slice(0, pipe).trim(), route: route || null };
  }
  return { heading: label, route: null };
}

/** Short chip label — "Stage 3" → "S3". */
export function stageChipLabel(label: string): string {
  const { heading } = parseStageLabel(label);
  const m = heading.match(/stage\s*(\d+)/i);
  if (m) return `S${m[1]}`;
  return heading.length > 6 ? heading.slice(0, 6) : heading;
}

/** Contextual status pill — OPEN reflects pick progress and whether the ladder still edits. */
export function stageStatusPill(
  phase: StagePhaseTone,
  opts?: {
    pickCount: number;
    picksRequired: number;
    isCurrentStage: boolean;
    entryEditable?: boolean;
  }
): { label: string; className: string } {
  const base = STAGE_PHASE_PILL[phase];
  if (phase !== 'open' || !opts) return base;

  const { pickCount, picksRequired, isCurrentStage, entryEditable = true } = opts;
  if (pickCount >= picksRequired) {
    if (!entryEditable || !isCurrentStage) {
      return { label: 'Locked in', className: 'bg-[#EFEFF3] text-ink2' };
    }
    return { label: 'All set', className: base.className };
  }
  return base;
}

/** Date + roll-out time — lifecycle lives in the status pill, not here. */
export function stageSubtitle(round: Round): string {
  const date = shortDay(round.firstKickoffAt);
  const time = timeLabel(round.firstKickoffAt);

  switch (round.status) {
    case 'OPEN':
    case 'SCHEDULED':
      return `${date} · roll-out ${time}`;
    case 'LOCKING':
      return `${date} · started ${time}`;
    case 'SETTLING':
      return `${date} · scoring`;
    default:
      return date;
  }
}
